import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createModifierGroupApi,
  deleteModifierGroupApi,
  getModifierGroupApi,
  getModifierGroupsApi,
  updateModifierGroupApi,
} from "../api/modifierGroupsApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { ModifierGroupId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateModifierGroup,
  RestUpdateModifierGroup,
} from "@KassaroAB/shared/types/modifierGroup";

const modifierGroupsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "modifierGroups");

const invalidateModifierGroupQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({
    queryKey: modifierGroupsQueryKey(scope),
  });
};

export const useGetModifierGroupsQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: modifierGroupsQueryKey(scope),
    queryFn: () => getModifierGroupsApi(scope!),
    enabled: !!scope,
  });
};

export const useGetModifierGroupQuery = (
  scope: CatalogScope | null,
  modifierGroupId: ModifierGroupId | null | undefined,
) => {
  return useQuery({
    queryKey: [...modifierGroupsQueryKey(scope), modifierGroupId],
    queryFn: () => getModifierGroupApi(scope!, modifierGroupId!),
    enabled: !!scope && !!modifierGroupId,
  });
};

export const useCreateModifierGroupMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (group: RestCreateModifierGroup) =>
      createModifierGroupApi(scope!, group),
    onSuccess: () => {
      invalidateModifierGroupQueries(queryClient, scope);
    },
  });
};

export const useUpdateModifierGroupMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      modifierGroupId,
      group,
    }: {
      modifierGroupId: ModifierGroupId;
      group: RestUpdateModifierGroup;
    }) => updateModifierGroupApi(scope!, modifierGroupId, group),
    onSuccess: () => {
      invalidateModifierGroupQueries(queryClient, scope);
    },
  });
};

export const useDeleteModifierGroupMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modifierGroupId: ModifierGroupId) =>
      deleteModifierGroupApi(scope!, modifierGroupId),
    onSuccess: () => {
      invalidateModifierGroupQueries(queryClient, scope);
    },
  });
};
